// Mobx
import { observable, action, observe, computed, intercept } from "mobx";
// Api
// RootStore
import { Store } from "#/store";
import { Api } from "#/api";
import { TypeSelectionResult } from "#/common/models/type_selection/TypeSelectionResult";
import { CatalogSelectionResult } from "#/common/models/type_selection/CatalogSelectionResult";

// Models

export class TypeSelectionFilterState {
  @observable
  public klasse?: number;

  @observable
  public filter = observable.map({});

  @observable
  public sa_data = observable.map({});

  @observable
  public codechain = observable.map({});

  @observable
  public typeSelection?: TypeSelectionResult;

  @observable
  public catalogSelection?: CatalogSelectionResult;

  @observable
  isTypeSelectionLoading = false;

  @observable
  isCatalogSelectionLoading = false;

  private token?: string;

  // RootStore
  public rootStore: Store;

  public constructor(store: Store) {
    this.rootStore = store;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("token")) {
      this.token = urlParams.get("token")!;
    }
  }

  @computed
  public get activeCatalogs() {
    if (this.catalogSelection && this.catalogSelection.entries) {
      return this.catalogSelection.entries
        .filter((entry) => entry.required_filter_satisfied)
        .filter(
          (entry) =>
            entry.count > 0 ||
            (entry.count == 0 && entry.count_nouserfilter == 0)
        )
        .map((entry) => entry.id);
    } else {
      return [];
    }
  }

  public getCatalogIds() {
    if (this.catalogSelection && this.catalogSelection.entries) {
      return this.catalogSelection.entries.map((entry) => entry.id);
    } else {
      return [];
    }
  }

  @computed
  public get selectFields() {
    if (this.typeSelection && this.typeSelection.selects) {
      return this.typeSelection.selects;
    } else {
      return [];
    }
  }

  @action.bound
  public async loadFilterSession() {
    if (this.token) {
      let session = await Api.katalogService.getSession(this.token);

      if (session && session["klasse"]) {
        if (session["klasse"] != this.klasse) {
          this.klasse = session["klasse"];
        }
      } else {
        this.klasse = undefined;
      }

      if (session && session["filter"]) {
        if (session["filter"] != this.filter) {
          this.filter.replace(session["filter"]);
        }
      } else {
        this.filter.replace({});
      }

      if (session && session["sa_data"]) {
        if (session["sa_data"] != this.sa_data) {
          this.sa_data.replace(session["sa_data"]);
        }
      } else {
        this.sa_data.replace({});
      }

      if (session && session["codechain"]) {
        if (session["codechain"] != this.codechain) {
          this.codechain.replace(session["codechain"]);
        }
      } else {
        this.codechain.replace({});
      }
    }
  }

  @action.bound
  public async loadTypeSelection() {
    if (this.klasse) {
      this.isTypeSelectionLoading = true;
      this.isCatalogSelectionLoading = true;

      this.typeSelection = await Api.katalogService.getTypeSelection(
        this.klasse,
        this.filter,
        this.getCompleteFilter(this.filter)
      );

      this.isTypeSelectionLoading = false;

      this.catalogSelection = await Api.katalogService.getCatalogSelection(
        this.klasse,
        this.filter,
        this.getCompleteFilter(this.filter)
      );

      this.isCatalogSelectionLoading = false;
    }

    if (this.token) {
      let data = {
        klasse: this.klasse,
        filter: this.filter,
        completeFilter: this.getCompleteFilter(this.filter),
      };

      if (Object.keys(this.codechain).length > 0) {
        data["codechain"] = this.codechain;
      }

      if (Object.keys(this.sa_data).length > 0) {
        data["sa_data"] = this.sa_data;
      }

      await Api.katalogService.updateSession(this.token, data);
    }
  }

  @action.bound
  public setFilterValue(key, value) {
    this.filter.set(key, value);
    this.codechain.replace({});
    this.sa_data.replace({});
    return this.updateFilter(this.filter.toJSON());
  }

  @action.bound
  public async updateFilter(filter) {
    if (this.klasse) {
      this.isCatalogSelectionLoading = true;
      this.isTypeSelectionLoading = true;

      this.typeSelection = await Api.katalogService.getTypeSelection(
        this.klasse,
        filter,
        this.getCompleteFilter(filter)
      );

      this.isTypeSelectionLoading = false;

      this.catalogSelection = await Api.katalogService.getCatalogSelection(
        this.klasse,
        filter,
        this.getCompleteFilter(filter)
      );

      this.isCatalogSelectionLoading = false;
    }

    if (this.token) {
      let data = {
        klasse: this.klasse,
        filter: this.filter,
        completeFilter: this.getCompleteFilter(this.filter),
      };

      if (Object.keys(this.codechain).length > 0) {
        data["codechain"] = this.codechain;
      }

      if (Object.keys(this.sa_data).length > 0) {
        data["sa_data"] = this.sa_data;
      }

      await Api.katalogService.updateSession(this.token, data);
    }
  }

  private getCompleteFilter(filter) {
    let f = JSON.parse(JSON.stringify(filter));
    let result = {};
    if (this.typeSelection) {
      this.typeSelection.selects.forEach((select) => {
        let value: any = null;
        if (select.data.length == 1) {
          value = select.data[0].key;
        } else if (f[select.key]) {
          value = f[select.key];
        }

        if (value) {
          result[select.key] = value;
        }
      });
    }

    return result;
  }

  public getToken() {
    return this.token;
  }

  public hasError(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      if (
        entry &&
        entry["required_message"] &&
        entry["required_message"].length > 0
      ) {
        return true;
      }
    }

    return false;
  }

  public getError(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      if (
        entry &&
        entry["required_message"] &&
        entry["required_message"].length > 0
      ) {
        return entry["required_message"];
      }
    }

    return "";
  }

  public getCount(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      if (entry && entry.hasOwnProperty("count")) {
        return entry.count;
      }
    }

    return 0;
  }

  public hasData(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      return (
        entry &&
        (entry.count > 0 || (entry.count == 0 && entry.count_nouserfilter == 0))
      );
    } else {
      return false;
    }
  }

  public isFilterSatisfied(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      return entry && entry.required_filter_satisfied;
    } else {
      return false;
    }
  }
}
